import { useParams, useNavigate } from 'react-router-dom';
import { AlertCircle, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useSegmentBuilder } from '../hooks/useSegmentBuilder';
import { useSegmentBuilderStore } from '@/stores/segmentBuilder.store';
import { CriteriaSection } from './components/CriteriaSection';
import { EstimationSection } from './components/EstimationSection';
import { AppTitle } from '@/components/app-header/app-header';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator
} from '@/components/ui/breadcrumb';
import { slugToMerchantId } from '@/utils/util';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { SelectActivityStep, type Activity } from '../components/dialog-steps/SelectActivityStep';
import { CountOperator, TimeBoundaryType } from '@/gql/graphql';

export default function SegmentBuilderPage() {
  const { segmentId, slug } = useParams<{ segmentId: string; slug: string }>();
  const navigate = useNavigate();

  const {
    loading: fetchLoading,
    fetchError,
    handleSave,
    refreshEstimateHandler,
    addConditionDialogState,
    openAddConditionDialog,
    closeAddConditionDialog
  } = useSegmentBuilder(segmentId, slugToMerchantId(slug!));

  const name = useSegmentBuilderStore((state) => state.name);
  const description = useSegmentBuilderStore((state) => state.description);
  const groups = useSegmentBuilderStore((state) => state.groups);
  const isDirty = useSegmentBuilderStore((state) => state.isDirty);
  const isSaving = useSegmentBuilderStore((state) => state.isSaving);
  const isInitialized = useSegmentBuilderStore((state) => state.isInitialized);

  const addConditionToGroup = useSegmentBuilderStore((state) => state.addConditionToGroup);
  const setStoreGroupOperator = useSegmentBuilderStore((state) => state.setGroupOperator);
  const removeStoreConditionFromGroup = useSegmentBuilderStore(
    (state) => state.removeConditionFromGroup
  );
  const updateStoreConditionInGroup = useSegmentBuilderStore(
    (state) => state.updateConditionInGroup
  );

  const handleCancel = () => {
    void navigate(`/merchants/${slug!}/segments`);
  };

  const handleActivitiesSelected = (selectedActivities: Activity[]) => {
    if (!addConditionDialogState.targetGroupClientId) {
      console.error('Cannot add conditions: No target group selected');
      return;
    }
    const targetGroupClientId = addConditionDialogState.targetGroupClientId;

    selectedActivities.forEach((activity) => {
      const conditionInput = {
        eventName: activity.id,
        operator: CountOperator.GreaterThanOrEqualTo,
        numTimes: 1,
        timeBoundary: { type: TimeBoundaryType.AnyTime }
      };
      addConditionToGroup(targetGroupClientId, conditionInput);
    });

    closeAddConditionDialog();
  };

  if (fetchLoading) {
    return (
      <div className="p-8 flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (fetchError) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-10 w-10 text-destructive mb-4" />
        <h2 className="text-lg font-semibold text-destructive">Failed to load segment</h2>
        <p className="mt-1 text-sm text-muted-foreground max-w-md">
          {fetchError.message || 'An unexpected error occurred.'}
        </p>
      </div>
    );
  }

  if (isInitialized && !name && groups.length === 0) {
    return (
      <div className="p-8 flex flex-col items-center justify-center h-64 text-center">
        <AlertCircle className="h-10 w-10 text-muted-foreground mb-4" />
        <h2 className="text-lg font-semibold">Segment not found or empty</h2>
        <p className="mt-1 text-sm text-muted-foreground max-w-md">
          Could not find segment with ID <span className="font-mono">{segmentId}</span> or it is
          empty.
        </p>
      </div>
    );
  }

  const breadcrumbContent = (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem>
          <BreadcrumbLink href={`/merchants/${slug!}/segments`}>Segments</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        <BreadcrumbItem>
          <BreadcrumbPage>{isInitialized ? name : 'Loading...'}</BreadcrumbPage>
        </BreadcrumbItem>
      </BreadcrumbList>
    </Breadcrumb>
  );

  return (
    <div className="p-6 md:p-8 space-y-6">
      <AppTitle>{breadcrumbContent}</AppTitle>

      <div className="flex justify-between items-start pt-1 pb-4 border-b">
        <div className="space-y-0.5">
          <h1 className="text-2xl font-semibold tracking-tight">
            {isInitialized ? name : 'Loading...'}
          </h1>
          <p className="text-sm text-muted-foreground max-w-xl">
            {isInitialized ? description || 'No description.' : 'Loading...'}
          </p>
        </div>
        <div className="flex items-center space-x-2 flex-shrink-0">
          <Button variant="outline" onClick={handleCancel} disabled={isSaving}>
            Cancel
          </Button>
          <Button
            onClick={() => {
              void handleSave();
            }}
            disabled={!isDirty || isSaving}
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 lg:gap-8">
        <div className="lg:col-span-2 space-y-6">
          <CriteriaSection
            groups={groups}
            onOpenAddConditionDialog={openAddConditionDialog}
            onSetGroupOperator={setStoreGroupOperator}
            onRemoveConditionFromGroup={removeStoreConditionFromGroup}
            onUpdateConditionInGroup={updateStoreConditionInGroup}
          />
        </div>
        <div className="lg:col-span-1 space-y-6">
          <EstimationSection onRefresh={refreshEstimateHandler} />
        </div>
      </div>

      <Dialog open={addConditionDialogState.isOpen} onOpenChange={closeAddConditionDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <SelectActivityStep
            onBack={closeAddConditionDialog}
            onAddConditions={handleActivitiesSelected}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
